import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Compass, BookOpen, Users, Heart, ArrowRight, Plus, Download, RefreshCw,
  Lock, X, MapPin, Edit3
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

import { CreatorLinks } from './components/CreatorLinks';
import {
  getPresses, pressWisdomCard, tendCard, getMyBearings, getResonances, getInsights, postEvent
} from './lib/api';
import {
  EMOTIONS, SOURCE_TYPES, GRANULARITIES, ACTION_READINESSES,
} from './lib/types';
import type {
  WisdomPress, Granularity, Emotion, PressInput, Tab
} from './lib/types';

const LS_TOKEN = 'bearings:pilgrim-token';
const LS_DRAFT = 'bearings:press-draft';

export default function Bearings() {
  const [tab, setTab] = useState<Tab>('atlas');
  const [presses, setPresses] = useState<WisdomPress[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<any>({});
  const [search, setSearch] = useState('');
  const [selectedCard, setSelectedCard] = useState<WisdomPress | null>(null);
  const [resonances, setResonances] = useState<any[]>([]);
  const [showResonances, setShowResonances] = useState(false);

  const [pilgrimToken, setPilgrimToken] = useState<string | null>(null);
  const [myPresses, setMyPresses] = useState<any[]>([]);
  const [myLoading, setMyLoading] = useState(false);

  const [insights, setInsights] = useState<any>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Press flow state
  const [showPress, setShowPress] = useState(false);
  const [pressStep, setPressStep] = useState(0);
  const [pressInput, setPressInput] = useState<Partial<PressInput>>({
    source_type: 'sermon',
    granularity: 'congregation',
    before_intensity: 3,
    after_intensity: 3,
    action_readiness: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [justPressedId, setJustPressedId] = useState<string | null>(null);

  // Tend state
  const [tendingId, setTendingId] = useState<string | null>(null);
  const [tendAfter, setTendAfter] = useState<Emotion>('tender');
  const [tendIntensity, setTendIntensity] = useState(3);
  const [tendNote, setTendNote] = useState('');

  // Load pilgrim token
  useEffect(() => {
    const saved = localStorage.getItem(LS_TOKEN);
    if (saved) setPilgrimToken(saved);
  }, []);

  // Load map
  async function loadMap(extraFilters: any = {}) {
    setLoading(true);
    try {
      const data = await getPresses({ ...filters, ...extraFilters, search: search || undefined });
      setPresses(data.presses || []);
    } catch (e) {
      toast.error('Could not load the map right now');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, search]);

  // Load insights when tab active
  async function loadInsights() {
    setInsightsLoading(true);
    try {
      const data = await getInsights();
      setInsights(data);
    } catch {
      toast.error('Insights are quiet at the moment');
    } finally {
      setInsightsLoading(false);
    }
  }

  useEffect(() => {
    if (tab === 'insights' && !insights) loadInsights();
  }, [tab]);

  // Load my bearings when tab or token changes
  async function loadMy() {
    if (!pilgrimToken) {
      setMyPresses([]);
      return;
    }
    setMyLoading(true);
    try {
      const data = await getMyBearings(pilgrimToken);
      setMyPresses(data.presses || []);
      if (data.pilgrim_token && data.pilgrim_token !== pilgrimToken) {
        localStorage.setItem(LS_TOKEN, data.pilgrim_token);
        setPilgrimToken(data.pilgrim_token);
      }
    } catch {
      toast.error('Could not retrieve your bearings');
    } finally {
      setMyLoading(false);
    }
  }

  useEffect(() => {
    if (tab === 'my') loadMy();
  }, [tab, pilgrimToken]);

  // Open resonances for a card
  async function openResonances(card: WisdomPress) {
    setSelectedCard(card);
    setShowResonances(true);
    setResonances([]);
    try {
      const data = await getResonances(card.id);
      setResonances(data.resonances || []);
      postEvent('resonance_searched', { value: (data.resonances || []).length });
    } catch {}
  }

  // The Press flow
  function openPress() {
    const draft = localStorage.getItem(LS_DRAFT);
    if (draft) {
      try { setPressInput(JSON.parse(draft)); } catch {}
    }
    setShowPress(true);
    setPressStep(0);
    postEvent('press_opened');
  }

  function updatePress(field: string, value: any) {
    const next = { ...pressInput, [field]: value };
    setPressInput(next);
    localStorage.setItem(LS_DRAFT, JSON.stringify(next));
  }

  function toggleAction(a: string) {
    const current = pressInput.action_readiness || [];
    const next = current.includes(a) ? current.filter(x => x !== a) : [...current, a];
    updatePress('action_readiness', next);
  }

  function setIntensity(which: 'before' | 'after', v: number) {
    if (which === 'before') updatePress('before_intensity', v);
    else updatePress('after_intensity', v);
  }

  async function submitPress() {
    if (!pressInput.claim_summary || pressInput.claim_summary.length < 12) {
      toast.error('Please describe the source of confusion or mistrust a little more.');
      return;
    }
    if (!pressInput.tension_text || pressInput.tension_text.length < 10) {
      toast.error('Naming the tension is the heart of this practice. What two things feel in conflict?');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...pressInput as any,
        pilgrim_token: pilgrimToken || undefined,
      };
      const res = await pressWisdomCard(payload);

      // Save token if new
      if (res.pilgrim_token) {
        localStorage.setItem(LS_TOKEN, res.pilgrim_token);
        setPilgrimToken(res.pilgrim_token);
      }

      setJustPressedId(res.id);
      toast.success('Card pressed into the commons.', { description: 'Your honesty becomes part of the shared map.' });

      // Clear draft
      localStorage.removeItem(LS_DRAFT);
      setPressInput({
        source_type: 'sermon', granularity: 'congregation',
        before_intensity: 3, after_intensity: 3, action_readiness: [],
      });
      setShowPress(false);
      setPressStep(0);

      // Reload map and animate the new one in
      await loadMap();
      setTimeout(() => setJustPressedId(null), 1400);

      // Switch to atlas so they see it
      setTab('atlas');
      postEvent('press_created');
    } catch (e: any) {
      toast.error(e.message || 'The press did not take. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Tend / evolve one of my cards
  async function submitTend(pressId: string) {
    if (!pilgrimToken) return;
    try {
      await tendCard(pressId, {
        after_emotion: tendAfter,
        after_intensity: tendIntensity,
        evolution_note: tendNote.trim() || undefined,
        pilgrim_token: pilgrimToken,
      });
      toast.success('Your bearing has been tended.', { description: 'The map now carries this change.' });
      setTendingId(null);
      setTendNote('');
      await loadMy();
      await loadMap(); // so public view also updates
      postEvent('card_tended');
    } catch (e: any) {
      toast.error(e.message || 'Could not update this card.');
    }
  }

  function startTending(p: any) {
    setTendingId(p.id);
    setTendAfter(p.after_emotion as Emotion);
    setTendIntensity(p.after_intensity);
    setTendNote('');
  }

  // Filters
  function toggleFilter(key: string, val: any) {
    const next = { ...filters };
    if (next[key] === val) delete next[key];
    else next[key] = val;
    setFilters(next);
    postEvent('filter_applied');
  }

  const filteredPresses = useMemo(() => {
    let out = [...presses];
    if (search.length > 2) {
      const s = search.toLowerCase();
      out = out.filter(p =>
        p.claim_summary.toLowerCase().includes(s) ||
        (p.tension_text || '').toLowerCase().includes(s)
      );
    }
    return out;
  }, [presses, search]);

  // Simple conceptual atlas positions (stable per id)
  function getAtlasPosition(p: WisdomPress) {
    const seed = p.id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    // X: factual <-> meaning (source_type + tension presence)
    const factual = ['news', 'social'].includes(p.source_type) ? 0.2 : ['scripture', 'sermon'].includes(p.source_type) ? 0.75 : 0.5;
    const x = Math.min(0.92, Math.max(0.08, factual + ((seed % 7) - 3) * 0.04));
    // Y: local <-> public
    const granMap: Record<Granularity, number> = { local: 0.15, congregation: 0.32, tradition: 0.55, public: 0.78, human: 0.9 };
    const yBase = granMap[p.granularity] || 0.5;
    const y = Math.min(0.9, Math.max(0.1, yBase + ((seed % 5) - 2) * 0.035));
    return { left: `${x * 100}%`, top: `${y * 100}%` };
  }

  // Export the current view as image (the map + visible cards)
  async function exportMapImage() {
    const el = document.getElementById('atlas-export');
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 1.6, backgroundColor: '#f7f4ed' });
    canvas.toBlob((blob) => {
      if (blob) saveAs(blob, `bearings-${format(new Date(), 'yyyy-MM-dd')}.png`);
    });
    postEvent('map_exported');
  }

  const activeFiltersCount = Object.keys(filters).length + (search ? 1 : 0);

  return (
    <div className="min-h-screen pb-16 relative" style={{ zIndex: 1 }}>
      <Toaster position="top-center" richColors closeButton />

      {/* Header — calm, generous */}
      <header className="border-b border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-5 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--accent)] flex items-center justify-center">
              <Compass className="text-[var(--text-inverse)]" size={20} />
            </div>
            <div>
              <div className="font-semibold tracking-tight text-xl">bearings</div>
              <div className="text-[10px] text-[var(--text-subtle)] -mt-0.5">PUBLIC UNCERTAINTY MAP • FAITH COMMUNITIES</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => { setTab('press'); openPress(); }} className="btn btn-stamp text-sm flex items-center gap-2">
              <Plus size={15} /> PRESS A WISDOM CARD
            </button>
            <button onClick={() => setTab('my')} className="btn text-sm flex items-center gap-2">
              <Lock size={15} /> MY BEARINGS
            </button>
          </div>
        </div>

        {/* Nav tabs */}
        <div className="max-w-5xl mx-auto px-5 flex gap-1 text-sm border-t border-[var(--border)]">
          {([
            { key: 'atlas', label: 'The Atlas', icon: MapPin },
            { key: 'press', label: 'Press a Card', icon: BookOpen },
            { key: 'my', label: 'My Bearings', icon: Heart },
            { key: 'insights', label: 'The Commons', icon: Users },
          ] as const).map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key as Tab)}
                className={`flex items-center gap-1.5 px-4 py-2.5 border-b-2 transition-all ${active ? 'border-[var(--accent)] text-[var(--text)] font-medium' : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text)]'}`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
          <div className="flex-1" />
          <CreatorLinks variant="compact" />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 pt-8">
        {/* Intro — explains the practice without lecturing */}
        <div className="max-w-2xl mb-8">
          <p className="text-[15px] text-[var(--text-muted)] leading-relaxed">
            Public mistrust often grows from <span className="text-[var(--text)]">source confusion</span> — the same claim arriving from sermon, social post, family, and news, each carrying different weight.
            This is a place to press small, honest cards that name what confuses you, what it costs, and what you are becoming ready to do. Over time the map shows us our shared bearings.
          </p>
          <p className="text-xs text-[var(--text-subtle)] mt-2">No likes. No winners. Only what we are willing to hold together.</p>
        </div>

        {/* ATLAS — the public uncertainty map */}
        {tab === 'atlas' && (
          <div id="atlas-export">
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="section-title">THE CURRENT LANDSCAPE</div>
                <div className="text-2xl font-semibold tracking-tight mt-0.5">Public Uncertainty Map</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => loadMap()} className="btn text-xs flex items-center gap-1.5"><RefreshCw size={13} /> Refresh</button>
                <button onClick={exportMapImage} className="btn text-xs flex items-center gap-1.5"><Download size={13} /> Export view</button>
                <button onClick={openPress} className="btn btn-primary text-xs flex items-center gap-1.5"><Plus size={13} /> Press</button>
              </div>
            </div>

            {/* Conceptual 2-axis atlas */}
            <div className="atlas atlas-grid mb-4 h-[320px] md:h-[360px] w-full" style={{ background: 'var(--surface-elev)' }}>
              {/* Axes labels */}
              <div className="absolute left-3 top-3 text-[10px] text-[var(--text-subtle)]">← factual claim / external</div>
              <div className="absolute right-3 top-3 text-[10px] text-[var(--text-subtle)] text-right">meaning &amp; faithfulness →</div>
              <div className="absolute left-3 bottom-3 text-[10px] text-[var(--text-subtle)]">personal / local</div>
              <div className="absolute right-3 bottom-3 text-[10px] text-[var(--text-subtle)] text-right">public / tradition / human</div>

              {/* Grid center cross */}
              <div className="absolute left-1/2 top-4 bottom-4 w-px bg-[var(--border)]/60" />
              <div className="absolute top-1/2 left-4 right-4 h-px bg-[var(--border)]/60" />

              <AnimatePresence>
                {presses.slice(0, 42).map((p, idx) => {
                  const pos = getAtlasPosition(p);
                  const isNew = p.id === justPressedId;
                  return (
                    <motion.button
                      key={p.id}
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: isNew ? 1.4 : 1 }}
                      transition={{ delay: Math.min(idx * 0.008, 0.2) }}
                      style={pos}
                      className={`map-point ${selectedCard?.id === p.id ? 'selected' : ''}`}
                      onClick={() => openResonances(p)}
                      title={`${p.source_type} • ${p.before_emotion}→${p.after_emotion}`}
                    />
                  );
                })}
              </AnimatePresence>

              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[9px] text-[var(--text-subtle)] tracking-[1.5px] bg-[var(--surface-elev)] px-2 rounded">EACH DOT IS ONE PRESSED CARD • CLICK TO SEE RESONANCES</div>
            </div>

            {/* Filters — make tradeoffs and patterns legible */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="text-xs uppercase tracking-widest text-[var(--text-subtle)] mr-1">Filter the map</div>
              {GRANULARITIES.map(g => (
                <button key={g.value} onClick={() => toggleFilter('granularity', g.value)} className={`filter-chip ${filters.granularity === g.value ? 'active' : ''}`}>{g.label}</button>
              ))}
              <div className="w-px h-3 bg-[var(--border)] mx-1" />
              {SOURCE_TYPES.slice(0, 5).map(s => (
                <button key={s.value} onClick={() => toggleFilter('source_type', s.value)} className={`filter-chip ${filters.source_type === s.value ? 'active' : ''}`}>{s.label}</button>
              ))}
              <button onClick={() => toggleFilter('has_tension', '1')} className={`filter-chip ${filters.has_tension === '1' ? 'active' : ''}`}>Has named tension</button>

              {activeFiltersCount > 0 && (
                <button onClick={() => { setFilters({}); setSearch(''); }} className="text-xs text-[var(--text-subtle)] underline ml-1">clear</button>
              )}
            </div>

            {/* Search */}
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search claims or tensions…"
              className="input w-full mb-4 text-sm"
            />

            {/* Card field — the actual wisdom cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {loading && <div className="text-sm text-[var(--text-subtle)] col-span-full py-8 text-center">Loading the current bearings…</div>}
              {!loading && filteredPresses.length === 0 && (
                <div className="empty-state col-span-full p-8 text-center text-sm">No cards match these filters yet. Press the first one in this region.</div>
              )}
              {filteredPresses.map(p => (
                <div key={p.id} className={`wisdom-card p-4 ${p.id === justPressedId ? 'pressed' : ''}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <div className="uppercase text-[10px] tracking-[1px] text-[var(--text-subtle)]">{p.source_type} • {p.granularity}</div>
                      <div className="mt-1 text-[15px] leading-snug pr-8">{p.claim_summary}</div>
                    </div>
                    <div className="stamp">PRESSED</div>
                  </div>

                  {p.tension_text && (
                    <div className="mt-3 tension-line text-[13.5px]">{p.tension_text}</div>
                  )}

                  <div className="press-line" />

                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="text-[var(--text-subtle)]">before</span>
                    <span className="font-medium">{p.before_emotion}</span>
                    <span className="text-[var(--text-subtle)]">→</span>
                    <span className="font-medium">{p.after_emotion}</span>
                    <span className="text-[var(--text-subtle)]">now</span>

                    <span className="ml-2 text-[10px] px-1.5 py-px rounded bg-[var(--surface-2)] text-[var(--text-muted)]">
                      intensity {p.before_intensity}→{p.after_intensity}
                    </span>
                  </div>

                  {p.action_readiness && p.action_readiness.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.action_readiness.slice(0, 3).map((a: string, i: number) => (
                        <span key={i} className="action-chip text-xs">{a}</span>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between text-[11px] text-[var(--text-subtle)]">
                    <div>{formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}</div>
                    <div className="flex gap-3">
                      <button onClick={() => openResonances(p)} className="hover:text-[var(--accent)] flex items-center gap-1">resonances <ArrowRight size={12} /></button>
                      {pilgrimToken && p.pilgrim_hash && /* rough ownership hint */ null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRESS FLOW — the heart of the interface metaphor */}
        {tab === 'press' && (
          <div className="max-w-2xl">
            <div className="mb-6">
              <div className="section-title">THE PRACTICE</div>
              <div className="text-2xl font-semibold tracking-tight">Press a Wisdom Card</div>
              <p className="text-[var(--text-muted)] mt-2">This is not a place to resolve anything. It is a place to make the confusion and the cost legible, so others can help carry it.</p>
            </div>

            <button onClick={openPress} className="btn btn-stamp w-full py-4 text-base flex items-center justify-center gap-2 mb-8">
              BEGIN A NEW PRESS <ArrowRight />
            </button>

            <div className="text-xs text-[var(--text-subtle)] space-y-1 pl-1">
              <p>• Name the actual source and the claim as you heard it.</p>
              <p>• Before and after: how your body felt then, and how it feels now after sitting with it.</p>
              <p>• The tension: the two goods or two truths that seem to pull against each other. This is the most important sentence you will write here.</p>
              <p>• Action readiness: what this particular confusion is making you more willing to do (or stop doing).</p>
            </div>
          </div>
        )}

        {/* MY BEARINGS — private evolution view, memory made legible */}
        {tab === 'my' && (
          <div className="max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="section-title">YOUR OWN ORIENTATION WORK</div>
                <div className="text-2xl font-semibold tracking-tight">My Bearings</div>
              </div>
              {!pilgrimToken && <div className="text-xs text-[var(--text-subtle)] max-w-[260px] text-right">Press any card and your pilgrim token will be saved here automatically. You can return and tend what you have pressed as your sense of things changes.</div>}
            </div>

            {!pilgrimToken && (
              <div className="empty-state p-6 text-sm">You have not yet pressed a card from this browser, or your token is not saved. Press one card and your token will appear.</div>
            )}

            {pilgrimToken && (
              <div className="mb-3 text-[11px] font-mono text-[var(--text-subtle)]">Pilgrim token: {pilgrimToken}</div>
            )}

            {myLoading && <div className="text-sm py-8 text-center text-[var(--text-subtle)]">Retrieving your cards…</div>}

            <div className="space-y-4">
              {myPresses.map(p => (
                <div key={p.id} className="wisdom-card p-4">
                  <div className="text-sm text-[var(--text-subtle)]">{p.source_type} • pressed {formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}</div>
                  <div className="mt-1">{p.claim_summary}</div>
                  {p.tension_text && <div className="tension-line mt-2 text-[13.5px]">{p.tension_text}</div>}

                  <div className="mt-3 text-xs flex items-center gap-2">
                    <span>before: <span className="font-medium">{p.before_emotion}</span> ({p.before_intensity})</span>
                    <span>→</span>
                    <span>now: <span className="font-medium">{p.after_emotion}</span> ({p.after_intensity})</span>
                  </div>

                  {p.evolutions && p.evolutions.length > 1 && (
                    <div className="mt-3 space-y-2">
                      {p.evolutions.map((e: any, idx: number) => (
                        <div key={idx} className="evolution text-xs text-[var(--text-muted)]">
                          {format(new Date(e.created_at), 'MMM d, yyyy')} — {e.after_emotion} ({e.after_intensity}) {e.note && <span className="text-[var(--text)]">“{e.note}”</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {tendingId === p.id ? (
                    <div className="mt-4 border-t border-[var(--border)] pt-3 space-y-2">
                      <div className="text-xs text-[var(--text-subtle)]">How do you hold this now?</div>
                      <div className="flex flex-wrap gap-1.5">
                        {EMOTIONS.map(em => (
                          <button key={em} onClick={() => setTendAfter(em)} className={`emotion-btn text-xs ${tendAfter === em ? 'selected' : ''}`}>{em}</button>
                        ))}
                      </div>
                      <div className="flex gap-1 mt-1">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setTendIntensity(n)} className={`intensity-dot ${tendIntensity >= n ? 'active' : ''}`} />
                        ))}
                      </div>
                      <textarea value={tendNote} onChange={e => setTendNote(e.target.value)} placeholder="What changed? (optional, becomes part of the public card)" className="input text-sm h-16" />
                      <div className="flex gap-2">
                        <button onClick={() => submitTend(p.id)} className="btn btn-primary text-sm">Save this update to the commons</button>
                        <button onClick={() => setTendingId(null)} className="btn text-sm">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => startTending(p)} className="mt-3 text-xs btn flex items-center gap-1.5"><Edit3 size={13} /> Tend this card (update my after)</button>
                  )}
                </div>
              ))}
              {pilgrimToken && myPresses.length === 0 && <div className="text-sm text-[var(--text-muted)]">No cards yet under this token. Press one and it will appear here.</div>}
            </div>
          </div>
        )}

        {/* INSIGHTS — collective sensemaking, many small inputs turned legible */}
        {tab === 'insights' && (
          <div className="max-w-3xl">
            <div className="mb-5">
              <div className="section-title">TURNING MANY PRESSES INTO SHARED SENSE</div>
              <div className="text-2xl font-semibold tracking-tight">The Commons Right Now</div>
              <p className="text-[var(--text-muted)] mt-1.5 text-sm">Aggregates only. No individuals are visible. The point is to see what tensions are alive and what readiness is quietly emerging across many different communities.</p>
            </div>

            <button onClick={loadInsights} className="btn text-xs mb-4 flex items-center gap-1.5"><RefreshCw size={13} /> Refresh reflection</button>

            {insightsLoading && <div className="text-sm py-6 text-center text-[var(--text-subtle)]">Listening to the current presses…</div>}

            {insights && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Emotion journeys */}
                  <div className="insight-block">
                    <div className="uppercase text-xs tracking-widest text-[var(--text-subtle)] mb-2">Emotion journeys observed</div>
                    <div className="space-y-1 text-sm">
                      {insights.emotion_journeys?.slice(0, 6).map((j: any, i: number) => (
                        <div key={i} className="flex items-center gap-2">
                          <span>{j.before}</span> <ArrowRight size={12} className="text-[var(--text-subtle)]" /> <span className="font-medium">{j.after}</span>
                          <span className="text-[var(--text-subtle)] text-xs">({j.count})</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Readiness surface */}
                  <div className="insight-block">
                    <div className="uppercase text-xs tracking-widest text-[var(--text-subtle)] mb-2">Action readinesses emerging</div>
                    <div className="space-y-1 text-sm">
                      {insights.readiness_surface?.map((r: any, i: number) => (
                        <div key={i} className="flex justify-between"><span>“{r.action}”</span> <span className="text-[var(--text-subtle)]">{r.count}</span></div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tradeoffs / tensions — the heart of not oversimplifying */}
                <div className="mt-4 insight-block">
                  <div className="uppercase text-xs tracking-widest text-[var(--text-subtle)] mb-2">Living tradeoffs being named</div>
                  <div className="space-y-3">
                    {insights.common_tensions?.length ? insights.common_tensions.map((t: any, i: number) => (
                      <div key={i} className="tension-line text-[14.2px]">{t.tension} <span className="text-xs text-[var(--text-subtle)]">({t.count})</span></div>
                    )) : <div className="text-sm text-[var(--text-muted)]">Few named tensions yet. The next press can change that.</div>}
                  </div>
                </div>

                {/* Gentle AI-shaped reflection — always disclosed */}
                <div className="mt-4 p-4 rounded bg-[var(--surface)] border border-[var(--border)] text-[13.8px] leading-relaxed">
                  {insights.cluster_reflection}
                  <div className="text-[10px] text-[var(--text-subtle)] mt-3">— shaped with a language model, offered only as a mirror. Not authoritative.</div>
                </div>

                <div className="text-right text-xs text-[var(--text-subtle)] mt-2">{insights.total_presses} presses in the current map</div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Press modal / flow — "wisdom card press" in action */}
      <AnimatePresence>
        {showPress && (
          <div className="fixed inset-0 bg-black/50 z-[70] flex items-start justify-center pt-10 px-4 overflow-auto" onClick={() => setShowPress(false)}>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ type: 'spring', bounce: 0.1, duration: 0.4 }}
              onClick={e => e.stopPropagation()}
              className="bg-[var(--surface-elev)] border border-[var(--border)] w-full max-w-[620px] rounded-lg p-6 md:p-8 mb-12"
            >
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div className="section-title">WISDOM CARD PRESS</div>
                  <div className="text-xl font-semibold tracking-tight">What confusion are you willing to name?</div>
                </div>
                <button onClick={() => setShowPress(false)} className="btn-ghost p-1"><X size={18} /></button>
              </div>

              {/* Stepper */}
              <div className="flex gap-2 mb-5 text-[11px] text-[var(--text-subtle)]">
                {['Source &amp; Claim', 'Before', 'The Tension', 'After &amp; Readiness'].map((s, i) => (
                  <div key={i} className={`px-2 py-0.5 rounded ${pressStep === i ? 'bg-[var(--accent)] text-[var(--text-inverse)]' : ''}`}>{i + 1}. {s.replace(/&amp;/g, '&')}</div>
                ))}
              </div>

              {/* Step 0: Source + claim */}
              {pressStep === 0 && (
                <div className="space-y-4">
                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-1">Where did this come from?</div>
                    <div className="flex flex-wrap gap-1.5">
                      {SOURCE_TYPES.map(st => (
                        <button key={st.value} onClick={() => updatePress('source_type', st.value)} className={`granularity-pill ${pressInput.source_type === st.value ? 'active' : ''}`}>{st.label}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-1">The claim or teaching that is causing confusion or mistrust (as you heard it)</div>
                    <textarea value={pressInput.claim_summary || ''} onChange={e => updatePress('claim_summary', e.target.value)} className="input h-24" placeholder="“If you really trusted God you wouldn’t need therapy…” or “The election was stolen and silence is complicity.”" />
                  </div>
                  <div className="flex justify-end"><button onClick={() => setPressStep(1)} className="btn btn-primary">Continue →</button></div>
                </div>
              )}

              {/* Step 1: Before */}
              {pressStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-2">When you first encountered this, what was the primary feeling in your body or spirit?</div>
                    <div className="flex flex-wrap gap-1.5">
                      {EMOTIONS.map(em => (
                        <button key={em} onClick={() => updatePress('before_emotion', em)} className={`emotion-btn ${pressInput.before_emotion === em ? 'selected' : ''}`}>{em}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-1.5">How heavy did it feel? (1 light — 5 heavy in the chest)</div>
                    <div className="flex gap-2">
                      {[1,2,3,4,5].map(n => <button key={n} onClick={() => setIntensity('before', n)} className={`intensity-dot ${ (pressInput.before_intensity||0) >= n ? 'active' : ''}`} />)}
                    </div>
                  </div>
                  <div className="flex justify-between"><button onClick={() => setPressStep(0)} className="btn">Back</button><button onClick={() => setPressStep(2)} className="btn btn-primary">Continue →</button></div>
                </div>
              )}

              {/* Step 2: The tension — making tradeoffs legible */}
              {pressStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-1">What two things are in tension here? (This is the most valuable sentence on the card.)</div>
                    <textarea value={pressInput.tension_text || ''} onChange={e => updatePress('tension_text', e.target.value)} className="input h-20" placeholder="Between taking the suffering of bodies seriously and honoring a theology of radical trust that has sustained our people through persecution." />
                    <div className="text-[10px] text-[var(--text-subtle)] mt-1">Examples: “Between fidelity to the tradition that formed me and not abandoning those it has harmed.” “Between prophetic clarity and the unity of people I actually know and love.”</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-1 mt-2">How wide is this uncertainty?</div>
                    <div className="flex flex-wrap gap-1.5">
                      {GRANULARITIES.map(g => (
                        <button key={g.value} onClick={() => updatePress('granularity', g.value)} className={`granularity-pill ${pressInput.granularity === g.value ? 'active' : ''}`}>{g.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between"><button onClick={() => setPressStep(1)} className="btn">Back</button><button onClick={() => setPressStep(3)} className="btn btn-primary">Continue →</button></div>
                </div>
              )}

              {/* Step 3: After + Readiness + submit */}
              {pressStep === 3 && (
                <div className="space-y-5">
                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-1.5">After sitting with this (prayer, conversation, time, more reading), how do you feel now?</div>
                    <div className="flex flex-wrap gap-1.5">
                      {EMOTIONS.map(em => (
                        <button key={em} onClick={() => updatePress('after_emotion', em)} className={`emotion-btn ${pressInput.after_emotion === em ? 'selected' : ''}`}>{em}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-1">Current intensity</div>
                    <div className="flex gap-2">
                      {[1,2,3,4,5].map(n => <button key={n} onClick={() => setIntensity('after', n)} className={`intensity-dot ${(pressInput.after_intensity||0) >= n ? 'active' : ''}`} />)}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-[var(--text-subtle)] mb-1.5">Because I am holding it this way, I am more ready to… (choose what fits)</div>
                    <div className="flex flex-wrap gap-1.5">
                      {ACTION_READINESSES.map(a => (
                        <button key={a} onClick={() => toggleAction(a)} className={`action-chip ${ (pressInput.action_readiness||[]).includes(a) ? 'selected' : '' }`}>{a}</button>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 flex items-center gap-2">
                    <button onClick={() => setPressStep(2)} className="btn">Back</button>
                    <button onClick={submitPress} disabled={submitting} className="btn btn-stamp flex-1">{submitting ? 'Pressing…' : 'PRESS THIS CARD INTO THE COMMONS'}</button>
                  </div>
                  <div className="text-[10px] text-center text-[var(--text-subtle)]">You will be given (or reuse) a pilgrim token so you can return and update how you hold this later. The card is otherwise anonymous.</div>
                </div>
              )}

              <div className="mt-6 text-[10px] text-center text-[var(--text-subtle)] border-t border-[var(--border)] pt-3">This practice exists because source confusion is real and costly. Your small act of naming helps others feel less alone in it.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Resonances drawer */}
      <AnimatePresence>
        {showResonances && selectedCard && (
          <div className="fixed inset-0 z-[80] flex" onClick={() => setShowResonances(false)}>
            <div className="flex-1" />
            <motion.div
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 30, opacity: 0 }}
              className="w-full max-w-md bg-[var(--surface-elev)] border-l border-[var(--border)] p-5 overflow-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between mb-3">
                <div className="font-medium">Resonances</div>
                <button onClick={() => setShowResonances(false)}><X /></button>
              </div>
              <div className="text-xs text-[var(--text-subtle)] mb-4">Cards that share similar tensions or sources. Similarity is statistical, never agreement.</div>

              <div className="wisdom-card p-3 mb-4 text-sm">{selectedCard.claim_summary}</div>

              {resonances.length === 0 && <div className="text-sm text-[var(--text-muted)]">No strong resonances found yet for this press. The map is still small.</div>}

              {resonances.map((r, i) => (
                <div key={i} className="wisdom-card p-3 mb-2 text-sm">
                  <div className="text-[var(--text-subtle)] text-xs">{r.press.source_type} • {r.press.granularity}</div>
                  <div>{r.press.claim_summary}</div>
                  {r.press.tension_text && <div className="tension-line mt-1 text-xs">{r.press.tension_text}</div>}
                  <div className="text-[10px] text-[var(--text-subtle)] mt-1">score {r.score.toFixed(2)}</div>
                </div>
              ))}

              <div className="text-[10px] text-[var(--text-subtle)] mt-4">These connections are offered so you can see you are not the only one sitting with something like this.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer with prominent creator links */}
      <footer className="max-w-5xl mx-auto px-5 mt-16">
        <CreatorLinks variant="footer" />
        <div className="text-center text-[10px] text-[var(--text-subtle)] mt-3 tracking-wider">Built with Cloudflare Pages, Workers, D1, KV, Vectorize, Workers AI, and Analytics Engine • All data is public but anonymous • A practice, not a platform</div>
      </footer>
    </div>
  );
}
